// MacroMarketHistoryService: Fetches and caches 7-day BTC/ETH dominance history
import axios from 'axios';

export interface DominanceHistoryPoint {
  timestamp: number;
  btcDominance: number;
  ethDominance: number;
}

export interface DominanceHistory {
  data: DominanceHistoryPoint[];
  lastUpdated: number;
}

const CACHE_TTL = 30 * 60 * 1000; // 30 minutes
const HISTORY_WINDOW = 7 * 24 * 60 * 60 * 1000; // 7 days in ms
let cache: DominanceHistory | null = null;
let cacheTimestamp = 0;

async function fetchDominanceHistory(): Promise<DominanceHistory> {
  try {
    const url = '/api/dominance-history';
    console.log('[BACK] fetchDominanceHistory: calling', url);
    const response = await axios.get(url);
    const rawHistory = response.data as DominanceHistory;
    const history: DominanceHistory = {
      data: Array.isArray(rawHistory?.data)
        ? rawHistory.data
            .map((pt) => ({
              timestamp: Number(pt.timestamp),
              btcDominance: Number(pt.btcDominance),
              ethDominance: Number(pt.ethDominance),
            }))
            .filter(
              (pt) => Number.isFinite(pt.timestamp)
                && Number.isFinite(pt.btcDominance)
                && Number.isFinite(pt.ethDominance),
            )
        : [],
      lastUpdated: Number(rawHistory?.lastUpdated) || Date.now(),
    };
    console.log('[BACK] fetchDominanceHistory result:', history);
    const now = Date.now();
    const filtered = history.data.filter(pt => now - pt.timestamp <= HISTORY_WINDOW);
    return { data: filtered, lastUpdated: history.lastUpdated };
  } catch (e) {
    // Fallback: return last cache if available
    if (cache) {
      console.log('[BACK] fetchDominanceHistory: using cache');
      return cache;
    }
    console.error('[BACK] fetchDominanceHistory error:', e);
    throw new Error('Unable to fetch dominance history');
  }
}

export async function getDominanceHistory(): Promise<DominanceHistory> {
  const now = Date.now();
  if (cache && now - cacheTimestamp < CACHE_TTL) {
    console.log('[BACK] getDominanceHistory: using cache');
    return cache;
  }
  console.log('[BACK] getDominanceHistory: calling fetchDominanceHistory');
  const history = await fetchDominanceHistory();
  cache = history;
  cacheTimestamp = now;
  console.log('[BACK] getDominanceHistory result:', history);
  return history;
}
